"use client";

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FunnelState } from "@/hooks/useFunnelState"; // Import FunnelState interface

interface UseFunnelNavigationProps {
  funnelState: FunnelState;
  getLocalizedPath: (pathKey: string) => string;
  handleResetFunnel: () => void;
}

export const useFunnelNavigation = ({
  funnelState,
  getLocalizedPath,
  handleResetFunnel,
}: UseFunnelNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const {
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    selectedProduct,
    finalPrice,
    contactFirstName, // Use contactFirstName
    contactLastName,  // Use contactLastName
    contactEmail,
    isOfferLoadingFromUrl,
    isHydrated,
  } = funnelState;

  // Navigation functions
  const goToHome = React.useCallback(() => {
    handleResetFunnel(); // Reset state
    // After resetting, navigate directly to the pet details page as the new starting point.
    navigate(getLocalizedPath('routes.petDetails'), { replace: true, state: { fromReset: true } });
  }, [handleResetFunnel, navigate, getLocalizedPath]);

  const goToPetDetails = React.useCallback(() => {
    navigate(getLocalizedPath('routes.petDetails'));
  }, [navigate, getLocalizedPath]);

  const goToProductVariantSelection = React.useCallback(() => {
    navigate(getLocalizedPath('routes.productVariantSelection'));
  }, [navigate, getLocalizedPath]);

  const goToProductCustomizationOptions = React.useCallback(() => {
    navigate(getLocalizedPath('routes.productCustomization'));
  }, [navigate, getLocalizedPath]);

  const goToContactDetails = React.useCallback(() => {
    navigate(getLocalizedPath('routes.addressDetails'));
  }, [navigate, getLocalizedPath]);

  const goToConfirmation = React.useCallback(() => {
    navigate(getLocalizedPath('routes.confirmation'));
  }, [navigate, getLocalizedPath]);

  // Effect for handling redirects based on funnel state
  React.useEffect(() => {
    console.log("useFunnelNavigation useEffect running. isOfferLoadingFromUrl:", isOfferLoadingFromUrl);
    console.log("Current path:", location.pathname);
    if (!isHydrated) {
      console.log("Skipping navigation checks until hydration completes.");
      return;
    }
    console.log("Dependencies:", { petType, petName, breed, birthDate, selectedBreedType, gender, selectedProduct, finalPrice, contactFirstName, contactLastName, contactEmail });

    if (isOfferLoadingFromUrl) {
      console.log("Skipping navigation checks because offer is loading from URL.");
      return;
    }

    const currentPath = location.pathname;
    const translatedHomePath = getLocalizedPath('routes.home');
    const translatedPetDetailsPath = getLocalizedPath('routes.petDetails');
    const translatedProductVariantSelectionPath = getLocalizedPath('routes.productVariantSelection');
    const translatedProductCustomizationPath = getLocalizedPath('routes.productCustomization');
    const translatedAddressDetailsPath = getLocalizedPath('routes.addressDetails');
    const translatedConfirmationPath = getLocalizedPath('routes.confirmation');

    const navState = location.state as any;

    // If we just came from a reset and are on the pet details page, do not redirect further.
    if (navState?.fromReset && currentPath === translatedPetDetailsPath) {
      console.log("On pet details page after reset. Skipping further auto-redirects.");
      return;
    }
    // If on pet details and petType is missing, this is the intended starting point after a reset, so do nothing.
    // No explicit redirect needed here for `translatedPetDetailsPath` if `!petType`.

    if (currentPath === translatedConfirmationPath &&
        selectedProduct && finalPrice !== null && contactFirstName && contactLastName && contactEmail && birthDate !== undefined) {
      console.log("Currently on confirmation page with all data, no redirect needed.");
      return;
    }

    else if (currentPath === translatedProductVariantSelectionPath && (!petType || !petName || !breed || birthDate === undefined || selectedBreedType === null || gender === null)) {
      console.log("Redirecting to /pet-details from /product-variant-selection due to missing pet details.");
      navigate(translatedPetDetailsPath);
    } else if (currentPath === translatedProductCustomizationPath && (!selectedProduct)) {
      console.log("Redirecting to /product-variant-selection from /product-customization due to missing product.");
      navigate(translatedProductVariantSelectionPath);
    } else if (currentPath === translatedAddressDetailsPath && (!selectedProduct || finalPrice === null)) {
      console.log("Redirecting to /product-customization from /address-details due to missing product/price.");
      navigate(translatedProductCustomizationPath);
    } else if (currentPath === translatedConfirmationPath && (!selectedProduct || finalPrice === null || !contactFirstName || !contactLastName || !contactEmail || birthDate === undefined)) {
      console.log("Redirecting to /address-details from /confirmation due to missing confirmation details.");
      navigate(translatedAddressDetailsPath);
    }
  }, [
    location.pathname, navigate, petType, petName, breed, birthDate, selectedBreedType, gender,
    selectedProduct, finalPrice, contactFirstName, contactLastName, contactEmail, isOfferLoadingFromUrl, getLocalizedPath, i18n, isHydrated
  ]);

  return {
    goToHome,
    goToPetDetails,
    goToProductVariantSelection,
    goToProductCustomizationOptions,
    goToContactDetails,
    goToConfirmation,
  };
};
